import { useEffect, useRef, useState } from "react";
import {
    Typography,
    Stack,
    Box,
    List,
    ListItem,
    ListItemText,
    Chip,
    Grid,
    Link,
    useTheme,
} from "@mui/material";
import PerformanceItemUpsertPopup from "../popups/PerformanceItemUpsertPopup";
import IncidentUpsertPopup from "../popups/IncidentUpsertPopup";
import VisibilityIcon from '@mui/icons-material/Visibility';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import { UpsertAction } from "../Enums";
import ConditionalDisplay from "../components/ConditionalDisplay";
import { Talent, ActiveState, PerformanceItem, IncidentReview, Incident, ScoreAdjustment } from "../models/dtos";
import TalentClient, { TalentErrors } from "../apiClients/TalentClient";
import { useErrorDisplay } from "../components/ErrorDisplay";
import { FormatDate, IsEmpty, IsNotEmpty, IsNullOrUndefined } from "../Helpers";
import useViewportMode from "../hooks/useViewportMode";
import { MobileEntityCard } from "../components/responsive";
import { I18N } from "../I18N";

export interface TalentEventManagementProps {
    talentId: string;
    subscriptionActive: boolean;
    inTalent?: Talent;
    onScoreAdjustment?: (scores: ScoreAdjustment) => void;
}

const TalentEventManagementComponent = ({ talentId, subscriptionActive, inTalent }: TalentEventManagementProps) => {

    const initialized = useRef<boolean>();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const theme = useTheme();
    const viewport = useViewportMode();
    const useCards = viewport.isPhone || (viewport.isTablet && viewport.isPortrait);

    const [talent, setTalent] = useState<Talent>(null);
    const [performanceItems, setPerformanceItems] = useState<PerformanceItem[]>([]);
    const [incidents, setIncidents] = useState<IncidentReview[]>([]);

    useEffect(() => {
        if (initialized.current === true)
            return;
        if (IsNullOrUndefined(talentId) || talentId === '')
            return;

        initialized.current = true;
        loadData();
    });

    const loadData = async () => {
        try {
            // setBusy(true);
            refreshData();


        } catch (error) {
            setError(error, TalentErrors.GetSingle);
        }
        finally {
            // setBusy(false);
        }
    };

    const handleResolve = async (id) => {
        new TalentClient().ResolvePerformanceItem(id);
        refreshData();
    }

    const transformIncidents = (incidents: Incident[]) => {

        let incidentDisplayList: IncidentReview[] = [];

        incidents.forEach(x => {
            let incidentReview = {
                Id: x.Id,
                PerformanceItemId: x.PerformanceItemId,
                IsDeleted: x.IsDeleted,
                LastUpdateDate: x.LastUpdateDate,
                TalentId: x.TalentId,
                Type: x.Type,
                Title: x.Title,
                Description: x.Description,
                OccurrenceDate: x.OccurrenceDate,
                apply: false,
                isReviewed: false
            };
            incidentDisplayList.push(incidentReview);
        });

        return incidentDisplayList;
    }

    const refreshData = async () => {
        let client = new TalentClient();

        let graph = await client.GetSingleGraph(talentId);
        setTalent(client.ExtractTalent(graph));
        setIncidents(transformIncidents(graph.Incidents));
        setPerformanceItems(graph.PerformanceItems);
    }

    const handleIncidentAdd = async (id) => {
        refreshData();
    }

    const getAlternateDisplayGridContainerStyle = () => {
        return { width: '100%', marginTop: theme.spacing(0) };
    }

    if (!subscriptionActive)
        if (!viewport.isDesktop)
            return <Box sx={{ p: 2 }}><Typography variant="body2">You need an active subscription for Events and Performance Challenges, <Link href="/SubscriptionPage" target="_self" rel="noopener" underline="hover">subscribe here</Link></Typography></Box>;
        else
        return (
            <>
                <Grid container spacing={1} sx={getAlternateDisplayGridContainerStyle}>
                    <Grid item xs={3}></Grid>
                    <Grid item xs={9}>
                        <Typography sx={{ fontSize: 'small', marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }}>
                            {`You need an active subscription for Events and Performance Challenges, `}
                            <Link href="/SubscriptionPage" target="_self" rel="noopener" underline="hover">{`subscribe here`}</Link>
                        </Typography>
                        
                    </Grid>
                </Grid>
            </>
        )
        else if (useCards)
    return (
        <Stack spacing={3} sx={{ p: 1.5 }}>
            <ErrorDisplay />
            <Stack spacing={1.5} component="section" aria-label="Performance improvement challenges">
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="h6">Performance Improvement Challenges</Typography>
                    <ConditionalDisplay ifTrue={incidents.some(x => IsNullOrUndefined(x.PerformanceItemId))}>
                        <Box sx={{ '& .MuiButtonBase-root': { minWidth: 44, minHeight: 44 } }}>
                            <PerformanceItemUpsertPopup mode={UpsertAction.Create} inTalent={talent} onAdd={() => refreshData()} hoverContent={I18N.Translate('Add Challenge')} visibleLabel={I18N.Translate('Add Challenge')} aria-label={I18N.Translate('Add Challenge')} />
                        </Box>
                    </ConditionalDisplay>
                </Stack>
                {performanceItems.length === 0 && <Typography variant="body2" color="text.secondary">No performance challenges.</Typography>}
                {performanceItems.map(item => (
                    <MobileEntityCard
                        key={item.Id}
                        title={item.Title}
                        status={item.State === ActiveState.Active ? 'Active' : 'Resolved'}
                        actions={item.State === ActiveState.Active ? (
                            <PerformanceItemUpsertPopup
                                mode={UpsertAction.Update}
                                inTalent={talent}
                                inPerformanceItem={item}
                                hoverContent="Mark Challenge Accomplished"
                                onResolve={(id) => handleResolve(id)}
                                icon={<PublishedWithChangesIcon htmlColor="red" />}
                            />
                        ) : (
                            <PerformanceItemUpsertPopup
                                mode={UpsertAction.View}
                                inTalent={talent}
                                inPerformanceItem={item}
                                hoverContent="View Performance Improvement Challenge"
                                icon={<VisibilityIcon htmlColor="lightblue" />}
                            />
                        )}
                        ariaLabel={`Performance challenge ${item.Title}`}
                    />
                ))}
            </Stack>
            <Stack spacing={1.5} component="section" aria-label="Events">
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="h6">Events</Typography>
                    <Box sx={{ '& .MuiButtonBase-root': { minWidth: 44, minHeight: 44 } }}>
                        <IncidentUpsertPopup onAdd={handleIncidentAdd} mode={UpsertAction.Create} inTalent={talent} inTalentId={talentId} hoverContent={I18N.Translate('Add Event')} visibleLabel={I18N.Translate('Add Event')} aria-label={I18N.Translate('Add Event')} />
                    </Box>
                </Stack>
                {incidents.length === 0 && <Typography variant="body2" color="text.secondary">No events.</Typography>}
                {incidents.map(incident => {
                    const eventType = (incident.Type ?? '').toLowerCase();
                    const isPositive = eventType === 'positive';
                    const isNegative = eventType === 'negative';
                    return (
                        <MobileEntityCard
                            key={incident.Id}
                            title={incident.Title}
                            subtitle={FormatDate(incident.OccurrenceDate)}
                            status={<Chip size="small" variant="outlined" color={isPositive ? 'success' : isNegative ? 'error' : 'default'} label={isPositive ? 'Positive' : isNegative ? 'Negative' : 'Unknown'} />}
                            actions={IsNotEmpty(incident.PerformanceItemId) ? (
                                <IncidentUpsertPopup mode={UpsertAction.View} inTalent={talent} inIncident={incident} hoverContent="View Event" icon={<VisibilityIcon htmlColor="lightblue" />} />
                            ) : (
                                <IncidentUpsertPopup mode={UpsertAction.Update} inTalent={talent} inIncident={incident} onChange={() => refreshData()} hoverContent="Edit Event" />
                            )}
                            ariaLabel={`Event ${incident.Title}`}
                        />
                    );
                })}
            </Stack>
        </Stack>
    );
        else
    return (
        <>
            <ErrorDisplay />
            {/* Performance Items */}
            <Stack direction='row' sx={{ alignItems: 'center' }}>
                <Typography>Performance Improvement Challenges</Typography>
                <ConditionalDisplay ifTrue={incidents.some(x => IsNullOrUndefined(x.PerformanceItemId))}>
                    <PerformanceItemUpsertPopup mode={UpsertAction.Create} inTalent={talent} onAdd={() => refreshData()} hoverContent={I18N.Translate('Add Challenge')} visibleLabel={I18N.Translate('Add Challenge')} aria-label={I18N.Translate('Add Challenge')} />
                </ConditionalDisplay>
            </Stack>
            <Stack>
                <Box sx={{ border: '1', borderColor: 'palette.divider' }}>
                    <List id='piiList' dense sx={{ '& .MuiButtonBase-root': { minWidth: viewport.isTablet ? 44 : undefined, minHeight: viewport.isTablet ? 44 : undefined } }}>
                        {performanceItems?.map(pii => (
                            <ListItem key={pii.Id}>
                                <ConditionalDisplay ifTrue={pii.State === ActiveState.Active}>
                                    <PerformanceItemUpsertPopup
                                        size='small'
                                        mode={UpsertAction.Update}
                                        inTalent={talent}
                                        inPerformanceItem={pii}
                                        hoverContent='Mark Challenge Accomplished'
                                        onResolve={(id) => handleResolve(id)}
                                        icon={<PublishedWithChangesIcon htmlColor='red' />} />
                                </ConditionalDisplay>
                                <ConditionalDisplay ifTrue={pii.State === ActiveState.Resolved}>
                                    <PerformanceItemUpsertPopup
                                        size='small' mode={UpsertAction.View}
                                        inTalent={talent} inPerformanceItem={pii}
                                        hoverContent='View Performance Improvement Challenge'
                                        icon={<VisibilityIcon htmlColor='lightblue' />} />
                                </ConditionalDisplay>
                                <ListItemText id={`${pii.Id}-item`} primary={pii.Title} />
                            </ListItem>
                        ))}
                    </List>
                </Box>
                {/* Events */}
                <Stack direction='row' sx={{ alignItems: 'center' }}>
                    <Typography>Events</Typography>
                    <IncidentUpsertPopup onAdd={handleIncidentAdd} mode={UpsertAction.Create} inTalent={talent} inTalentId={talentId} hoverContent={I18N.Translate('Add Event')} visibleLabel={I18N.Translate('Add Event')} aria-label={I18N.Translate('Add Event')} />
                </Stack>
                <Box sx={{ border: '1', borderColor: 'palette.divider' }}>
                    <List id='incidentList' dense sx={{ '& .MuiButtonBase-root': { minWidth: viewport.isTablet ? 44 : undefined, minHeight: viewport.isTablet ? 44 : undefined } }}>
                        {incidents?.map(i => {
                            const eventType = (i.Type ?? '').toLowerCase();
                            const isPositive = eventType === 'positive';
                            const isNegative = eventType === 'negative';

                            return (
                                <ListItem key={i.Id}>
                                <ConditionalDisplay ifTrue={IsNotEmpty(i.PerformanceItemId)}>
                                    <IncidentUpsertPopup mode={UpsertAction.View} inTalent={talent} inIncident={i} hoverContent='View Event' icon={<VisibilityIcon htmlColor='lightblue' />} />
                                </ConditionalDisplay>
                                <ConditionalDisplay ifTrue={IsEmpty(i.PerformanceItemId)}>
                                    <IncidentUpsertPopup mode={UpsertAction.Update} inTalent={talent} inIncident={i} onChange={() => refreshData()} hoverContent='Edit Event' />
                                </ConditionalDisplay>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    color={isPositive ? "success" : isNegative ? "error" : "default"}
                                    label={isPositive ? "Positive" : isNegative ? "Negative" : "Unknown"}
                                    sx={{ marginRight: theme.spacing(1) }}
                                />
                                <ListItemText id={`${i.Id}-item`} primary={i.Title} secondary={FormatDate(i.OccurrenceDate)} />
                            </ListItem>
                            );
                        })}
                    </List>
                </Box>
            </Stack>
        </>
    );
}

export default TalentEventManagementComponent;
